/**
 * Connection-test proof model. Four separate things, never collapsed into one status:
 *   requested provider target  — what FundElite asked DataImpulse for (authoritative)
 *   provider catalog count     — provider-listed IPs; never verifies an exit
 *   proxy routing              — networkStatus
 *   external geo observation   — third-party databases; never mutates the target
 */
import { formatUsStateLabel, stripQaSentinelText } from './fundingAuthorizedQaProxyService';

export const QA_NETWORK_STATUS = Object.freeze({
  CONFIRMED: 'confirmed',
  FAILED: 'failed',
  UNKNOWN: 'unknown',
});

export const QA_PROVIDER_TARGET_STATUS = Object.freeze({
  CONFIGURED: 'configured',
  MISSING: 'missing',
});

export const QA_EXTERNAL_GEO_STATUS = Object.freeze({
  MATCHED: 'matched',
  CITY_COMPATIBLE: 'city_compatible',
  STATE_COMPATIBLE: 'state_compatible',
  MISMATCH: 'mismatch',
  UNAVAILABLE: 'unavailable',
});

const EXTERNAL_GEO_FROM_LOCATION_STATUS = {
  matched: QA_EXTERNAL_GEO_STATUS.MATCHED,
  compatible: QA_EXTERNAL_GEO_STATUS.MATCHED,
  state_compatible: QA_EXTERNAL_GEO_STATUS.STATE_COMPATIBLE,
  city_compatible: QA_EXTERNAL_GEO_STATUS.CITY_COMPATIBLE,
  mismatch: QA_EXTERNAL_GEO_STATUS.MISMATCH,
};

const SENTINEL_RE = /^(__all_[a-z]+__|all|all[_ ](states|cities|zips|asns))$/i;

function specific(value) {
  const text = String(value ?? '').trim();
  return text && !SENTINEL_RE.test(text) ? text : '';
}

function titleCase(value) {
  const text = stripQaSentinelText(String(value || '').trim());
  if (!text) return '';
  return text === text.toLowerCase() ? text.replace(/\b\w/g, (ch) => ch.toUpperCase()) : text;
}

function zip5(value) {
  const match = String(value || '').match(/\d{5}/);
  return match ? match[0] : '';
}

export function deriveQaProofStatusModel(proof) {
  const raw = proof || {};
  const network = String(raw.networkStatus || '').toLowerCase();
  let networkStatus = QA_NETWORK_STATUS.UNKNOWN;
  if (raw.differentIp === true || network === 'confirmed') {
    networkStatus = QA_NETWORK_STATUS.CONFIRMED;
  } else if (raw.differentIp === false || network === 'failed') {
    networkStatus = QA_NETWORK_STATUS.FAILED;
  }
  const declaredTarget = String(raw.providerTargetStatus || '').toLowerCase();
  const providerTargetStatus = declaredTarget === QA_PROVIDER_TARGET_STATUS.MISSING
    || (!declaredTarget && !specific(raw.target?.country) && !raw.providerRequest?.targeting)
    ? QA_PROVIDER_TARGET_STATUS.MISSING
    : QA_PROVIDER_TARGET_STATUS.CONFIGURED;
  const declaredGeo = String(raw.externalGeoStatus || '').toLowerCase();
  const externalGeoStatus = Object.values(QA_EXTERNAL_GEO_STATUS).includes(declaredGeo)
    ? declaredGeo
    : EXTERNAL_GEO_FROM_LOCATION_STATUS[String(raw.locationStatus || '').toLowerCase()]
      || QA_EXTERNAL_GEO_STATUS.UNAVAILABLE;
  return { networkStatus, providerTargetStatus, externalGeoStatus };
}

/** Parsed sanitized provider suffix (`cr.us;state.california;zip.90044`); session ids dropped. */
export function parseProviderTargeting(suffix) {
  const parts = {};
  String(suffix || '').split(';').forEach((part) => {
    const index = part.indexOf('.');
    if (index <= 0) return;
    const key = part.slice(0, index).toLowerCase();
    if (key === 'sessid') return;
    parts[key] = part.slice(index + 1);
  });
  return parts;
}

export function sanitizeProviderTargeting(suffix) {
  return String(suffix || '')
    .split(';')
    .filter((part) => part && !/^sessid\./i.test(part))
    .join(';');
}

/** Most specific requested level: `ZIP 90044`, `San Francisco`, `California`, `US`. */
export function describeRequestedTargetLevel(proof) {
  const request = parseProviderTargeting(proof?.providerRequest?.targeting);
  const target = proof?.target || {};
  const zip = zip5(request.zip || specific(target.zip));
  if (zip) return `ZIP ${zip}`;
  const city = specific(request.city || target.city);
  if (city) return titleCase(city);
  const state = specific(request.state || target.state);
  if (state) return formatUsStateLabel(state) || titleCase(state);
  const country = specific(request.cr || target.country);
  return country ? country.toUpperCase() : '';
}

export function describeProviderTargeting(proof) {
  const level = describeRequestedTargetLevel(proof);
  return level ? `${level} requested` : 'Not configured';
}

/** `US / California / ZIP 90044` from the saved target (never from geo evidence). */
export function formatRequestedTargetPath(proof) {
  const target = proof?.target || {};
  const request = parseProviderTargeting(proof?.providerRequest?.targeting);
  const country = specific(target.country || request.cr);
  const state = specific(target.state || request.state);
  const city = specific(target.city || request.city);
  const zip = zip5(specific(target.zip) || request.zip);
  return [
    country ? country.toUpperCase() : '',
    state ? (formatUsStateLabel(state) || titleCase(state)) : '',
    city ? titleCase(city) : '',
    zip ? `ZIP ${zip}` : '',
  ].filter(Boolean).join(' / ');
}

export function formatExternalGeoRow(row) {
  const place = [titleCase(row?.city), row?.state].filter(Boolean).join(', ');
  const zip = zip5(row?.postal);
  const location = [place, zip ? `ZIP ${zip}` : ''].filter(Boolean).join(' · ') || 'no location';
  return `${row?.source || 'unknown source'}: ${location}`;
}

export function describeGeoConfidence(proof) {
  const { externalGeoStatus } = deriveQaProofStatusModel(proof);
  const level = describeRequestedTargetLevel(proof) || 'the QA target';
  const state = specific(proof?.target?.state);
  const stateLabel = state ? (formatUsStateLabel(state) || titleCase(state)) : '';
  const zipLevel = proof?.locationVerification?.mismatchLevel === 'zip';
  switch (externalGeoStatus) {
    case QA_EXTERNAL_GEO_STATUS.MATCHED:
      return { tone: 'ok', label: `✓ External geo sources confirm ${level}` };
    case QA_EXTERNAL_GEO_STATUS.CITY_COMPATIBLE:
      return { tone: 'warn', label: `External geo sources confirm the city but not ${level}` };
    case QA_EXTERNAL_GEO_STATUS.STATE_COMPATIBLE:
      return {
        tone: 'warn',
        label: `External geo sources do not confirm ${level}${stateLabel ? ` (${stateLabel} only)` : ''}`,
      };
    case QA_EXTERNAL_GEO_STATUS.MISMATCH:
      return zipLevel
        ? { tone: 'warn', label: `External geo sources report a different ZIP than ${level}` }
        : { tone: 'error', label: 'External geo sources place this exit outside the QA target' };
    default:
      return { tone: 'muted', label: 'External geo sources could not place this exit' };
  }
}

export function geoDisagreementHelper(proof) {
  const level = describeRequestedTargetLevel(proof) || 'unchanged';
  return 'Residential IP geolocation databases may disagree with the provider\u2019s targeting. '
    + `The QA target remains ${level}.`;
}
